import type { WireSpec } from "./types";
import type { FuseSelectionPolicy } from "./design-profiles/contracts";

export type { WireSpec };

/**
 * Marine-grade tinned copper, 105 °C insulation, single conductor, outside
 * engine spaces, no more than two conductors bundled — ABYC E-11 TABLE VI-A
 * (105 °C column; the same figures Blue Sea Systems publishes as "allowable
 * amperage of conductors"). Metric sizes take the ampacity of the next SMALLER
 * listed AWG (2.5 mm² ≈ 13.3 AWG → 14 AWG 35 A; 4 mm² ≈ 11.3 AWG → 12 AWG
 * 45 A; 50 mm² sits between 1 AWG and 1/0 → 1 AWG 245 A), so every entry is at
 * or below the table. Sizes below 16 AWG are grandfathered overrides only.
 *
 * Before 2026-08-28 this table sat ~25 % under TABLE VI-A with no citation; on
 * real user systems that alone produced false `fuse-exceeds-wire` criticals
 * (200 A on 50 mm², 350 A Class T on 95 mm²) and one wire step of oversize.
 */
export const WIRE_TABLE: WireSpec[] = [
  { mm2: 0.5, awg: "20",  ampacity: 8 },
  { mm2: 0.75, awg: "18", ampacity: 20 },
  { mm2: 1.0, awg: "17",  ampacity: 20 },
  { mm2: 1.5, awg: "16",  ampacity: 25 },
  { mm2: 2.5, awg: "13",  ampacity: 35 },
  { mm2: 4,   awg: "11",  ampacity: 45 },
  { mm2: 6,   awg: "10",  ampacity: 60 },
  { mm2: 10,  awg: "8",   ampacity: 80 },
  { mm2: 16,  awg: "6",   ampacity: 120 },
  { mm2: 25,  awg: "4",   ampacity: 160 },
  { mm2: 35,  awg: "2",   ampacity: 210 },
  { mm2: 50,  awg: "1",   ampacity: 245 },
  { mm2: 70,  awg: "2/0", ampacity: 330 },
  { mm2: 95,  awg: "3/0", ampacity: 385 },
  { mm2: 120, awg: "4/0", ampacity: 445 },
];

/**
 * ABYC E-11.14.2.6 "Voltage Drop": 3 % for panelboard main feeders, bilge
 * blowers, electronic equipment and navigation lights (TABLE IX); 10 % for
 * lighting other than navigation lights and other non-critical circuits (TABLE X).
 */
export const VDROP_PCT_CRITICAL = 3;
export const VDROP_PCT_NON_CRITICAL = 10;

/** Existing conservative planner target for starting loops, not a universal
 * ABYC limit or a manufacturer requirement for every engine. */
export const STARTING_VDROP_TARGET_PCT = 2;

/** Standard fuse ratings (Blue Sea / ANL / MIDI / MEGA / ATC blade superset). */
export const FUSE_RATINGS = [
  1, 2, 3, 5, 7.5, 10, 15, 20, 25, 30, 35, 40, 50, 60,
  70, 80, 100, 125, 150, 175, 200, 225, 250, 300, 350, 400, 500,
];

/** Resistivity of copper at 25°C (Ω·mm²/m). */
export const CU_RHO = 0.0175;

/** ABYC E-11 / NEC 310.15 continuous-load derating: a load carried for more
 *  than 3 hours needs a conductor with ampacity ≥ 1.25 × that load. */
export const CONTINUOUS_LOAD_HEADROOM = 1.25;

/**
 * ABYC E-11.4.1.1 minimum conductor for general boat DC wiring: 16 AWG
 * (≈ 1.5 mm²). A **mechanical** floor — chafe, vibration, terminal pull-out —
 * independent of the ampacity / V-drop result, and stricter than ISO 13297's
 * 1 mm², so the planner is compliant in either jurisdiction.
 */
export const MIN_GENERAL_CIRCUIT_MM2 = 1.5;

/** Sub-1.5 mm² entries stay in {@link WIRE_TABLE} so an older project's
 *  grandfathered override still renders — they are just never offered afresh. */
export const SELECTABLE_WIRE_TABLE: WireSpec[] = WIRE_TABLE.filter(
  (w) => w.mm2 >= MIN_GENERAL_CIRCUIT_MM2,
);

/** Native AWG areas for the standalone calculator and its pinned editor wires.
 * NBS Handbook 100, Copper Wire Tables: d(mm)=0.127×92^((36−AWG)/39).
 * https://nvlpubs.nist.gov/nistpubs/Legacy/hb/nbshandbook100.pdf
 * Ampacity deliberately takes the next SMALLER existing metric entry. This
 * conservative lower bound reuses the reviewed engine table; it does not
 * pretend that its approximate AWG labels are identical conductor areas.
 * Keep the engine's 1.5 mm² mechanical floor (so this pool starts at 14 AWG).
 * Existing projects' auto-selection remains the metric table. */
export const AWG_WIRE_TABLE: WireSpec[] = [14, 12, 10, 8, 6, 4, 2, 1, 0, -1, -2, -3].map(gauge => {
  const mm2 = Number((Math.PI / 4 * (0.127 * 92 ** ((36 - gauge) / 39)) ** 2).toFixed(5));
  const ampacity = WIRE_TABLE.filter(w => w.mm2 <= mm2).at(-1)!.ampacity;
  return { mm2, awg: gauge > 0 ? String(gauge) : `${1 - gauge}/0`, ampacity };
});

export function wireByMm2(mm2: number, table: readonly WireSpec[] = WIRE_TABLE, additional: readonly WireSpec[] = AWG_WIRE_TABLE): WireSpec {
  const known = table.find(w => w.mm2 === mm2) ?? additional.find(w => w.mm2 === mm2);
  if (known) return known;
  // Preserve a manual/imported area without inventing an ampacity formula.
  // The largest supported metric area no greater than it supplies a
  // conservative planning bound, not a newly certified conductor rating.
  // Exact native AWG entries above keep their existing reviewed values.
  // Below-table and invalid inputs have no supported positive ampacity;
  // above-table areas cannot extrapolate past the last known bound.
  const lower = Number.isFinite(mm2) && mm2 > 0
    ? table.filter(w => w.mm2 <= mm2).at(-1) : undefined;
  return { mm2, awg: "?", ampacity: lower?.ampacity ?? 0 };
}

/**
 * Wire cross-section for a continuous current and one-way length, so that
 * voltage drop ≤ maxDropPct AND ampacity ≥ `headroom × current`. Cranking
 * circuits are exempt from the 1.25× rule per ABYC E-11 — pass `headroom = 1`.
 *
 * `minAmpacity` is a floor independent of `currentA × headroom`: ABYC requires
 * fuse ≤ wire ampacity, so a small draw with a big inrush needs a wire that can
 * host the inrush fuse.
 */
export function selectWire(
  currentA: number,
  oneWayLengthM: number,
  voltage = 12,
  maxDropPct = 3,
  headroom = CONTINUOUS_LOAD_HEADROOM,
  minAmpacity = 0,
  ambientDerating = 1,
  /** Mechanical floor in mm² (ABYC E-11.4.1.1's 16 AWG). Pass `0` for internal
   *  wiring that legitimately uses thinner conductors. */
  minMm2 = MIN_GENERAL_CIRCUIT_MM2,
  table: readonly WireSpec[] = WIRE_TABLE,
): WireSpec {
  // Engine bay ≈ 0.82 at 50 °C for 105 °C wire (ABYC E-11 Table V). Moves only
  // the ampacity floor; V-drop is resistance × length × current.
  const derate = ambientDerating > 0 ? ambientDerating : 1;
  const candidates = table.filter((w) => w.mm2 >= minMm2);
  const pool = candidates.length > 0 ? candidates : table;
  if (currentA <= 0 || oneWayLengthM <= 0) {
    if (minAmpacity <= 0) return pool[0];
    for (const spec of pool) {
      if (spec.ampacity * derate >= minAmpacity) return spec;
    }
    return pool[pool.length - 1];
  }
  const maxDropV = (voltage * maxDropPct) / 100;
  const requiredMm2 = (2 * currentA * oneWayLengthM * CU_RHO) / maxDropV;
  const requiredAmpacity = Math.max(currentA * headroom, minAmpacity);

  for (const spec of pool) {
    if (spec.mm2 >= requiredMm2 && spec.ampacity * derate >= requiredAmpacity) {
      return spec;
    }
  }
  return pool[pool.length - 1];
}

export function voltageDrop(
  currentA: number,
  oneWayLengthM: number,
  wireMm2: number,
): number {
  if (wireMm2 <= 0) return Infinity;
  return (2 * currentA * oneWayLengthM * CU_RHO) / wireMm2;
}

/**
 * How far above its rating a fuse rides through a motor start or inverter
 * surge without opening. Published time-current envelopes: Littelfuse ATO/ATC
 * (257 series) opens at 135 % in 0.75–600 s and at 200 % in 0.15–5 s; Blue Sea
 * ANL and MRBF hold 100 % for 4 h and open at 135 % within 1 h, at 200 % within
 * ~10 s; MEGA/MIDI and Class T sit between. A sub-second inrush at 150 % is
 * inside every one of those hold windows, so a fuse is chosen for the
 * *continuous* load and only bumped when the surge would exceed 1.5 × its
 * rating. Not 1.25 × peak: that put a 1500 W inverter on a 400 A fuse and 4/0
 * wire where Victron's own manuals fuse a 1200–1600 W unit at 200–250 A.
 * `ampSurge` cannot say how long a surge lasts — a glow plug that pulls 10 A
 * for a minute is not an inrush — so a part with a manufacturer fuse figure
 * should carry it as an override rather than lean on this factor.
 */
export const SURGE_HOLD_FACTOR = 1.5;

/** Largest standard rating a conductor may host (ABYC E-11.10.1.2: OCPD ≤
 *  conductor ampacity), or 0 when even the 1 A rung exceeds it. */
export function largestFuseWithin(wireAmpacity: number, ratings: readonly number[] = FUSE_RATINGS): number {
  let last = 0;
  for (const f of ratings) {
    if (f <= wireAmpacity) last = f;
  }
  return last;
}

/** Smallest standard fuse ≥ 1.25 × continuous (NEC-style derating) that also
 *  holds the surge (`peakA ≤ SURGE_HOLD_FACTOR × fuse`) and is ≤ wire ampacity,
 *  so the fuse protects the wire.
 *
 *  Returns **0 — no fuse to recommend — when there is no current to rate for**
 *  (both `continuousA` and `peakA` ≤ 0). The ladder's first rung is not a
 *  recommendation: written onto a switch → battery lead whose ends resolved
 *  nothing, a 1 A fuse then read back as a 1 A load through the fuse fallback
 *  in `cable-current.ts`. 0 is the same sentinel `computeCableSpec` reports for
 *  an AC run, so every consumer already has a "no fuse" branch to take. */
export function recommendFuse(
  continuousA: number,
  wireAmpacity: number,
  peakA: number = continuousA,
  policy?: FuseSelectionPolicy,
): number {
  if (continuousA <= 0 && peakA <= 0) return 0;
  const ratings = policy?.fuseRatings ?? FUSE_RATINGS;
  const target = Math.max(continuousA * (policy?.continuousLoadHeadroom ?? 1.25), peakA / (policy?.surgeHoldFactor ?? SURGE_HOLD_FACTOR));
  for (const f of ratings) {
    if (f >= target && f <= wireAmpacity) return f;
  }
  return largestFuseWithin(wireAmpacity, ratings) || ratings[0];
}

/**
 * Cranking fuse: smallest standard rating ≥ max(1.25 × continuous, 250 A). Per
 * Compass Marine / MarineHowTo a small marine diesel (≤25 HP, ~180 A cranking)
 * takes a 250 A class-T.
 *
 * Wire ampacity is deliberately ignored: "OCPD rating ≤ conductor ampacity" is
 * ABYC E-11.10.2.3, an AC clause, and the DC side exempts cranking conductors
 * (E-11.10.1.1.1 EXCEPTION 1). `detectFuseExceedsWire` must skip them too, or it
 * raises a critical on this function's own output.
 */
export function recommendCrankingFuse(continuousA: number, policy?: FuseSelectionPolicy): number {
  if (policy && policy.crankingMinimumFuseA == null) return 0;
  const ratings = policy?.fuseRatings ?? FUSE_RATINGS;
  const target = Math.max(continuousA * (policy?.continuousLoadHeadroom ?? 1.25), policy?.crankingMinimumFuseA ?? 250);
  for (const f of ratings) {
    if (f >= target) return f;
  }
  return ratings[ratings.length - 1];
}
